import { ds } from "js/core/models/dataService";
import getLogger, { LogGroup } from "js/core/logger";
import renderReactRoot from "js/react/renderReactRoot";
import { $, Backbone, _ } from "js/vendor";
import ThemeEditor from "js/react/views/ThemeEditor/ThemeEditor";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import { controls } from "js/editor/ui";
import { ChooseThemeDialog } from "js/editor/dialogs/chooseThemeDialog";
import { Theme } from "js/core/models/theme";
import { SharedTheme } from "js/core/models/sharedTheme";
import { Slide } from "js/core/models/slide";
import { deepClone } from "js/core/utilities/extensions";
import { Palette } from "js/core/models/palette";
import { builtInFonts, obsoleteBuiltInFontIds } from "common/fontConstants";
import { FeatureType } from "js/core/models/features";
import React, { Fragment } from "react";
import "css/dialog.scss";
import "css/style-guide.scss";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import {
    PreviewSlide1,
    PreviewSlide2,
    PreviewSlide3,
    PreviewSlide4,
    PreviewSlide5,
    PreviewSlide6
} from "js/editor/previewSlides";
import { trackActivity } from "js/core/utilities/utilities";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { UIController } from "js/editor/dialogs/UIController";
import { BehaviorSubject, Subscription } from "rxjs";
import { appVersion } from "js/config";
import { getCanvasBundle } from "js/canvas";
import AppController from "js/core/AppController";

const logger = getLogger(LogGroup.EDITOR);

export const EditThemePanel = Backbone.View.extend({
    className: "modal_dialog edit_theme_dialog",
    lockSlide: true,

    initialize: function(options) {
        this.canvasBundleLoadPromise = getCanvasBundle(appVersion);

        this.isNewTheme = !!options.isNewTheme;

        this.originalTheme = options.theme;

        this.originalThemeJson = JSON.stringify(this.originalTheme.attributes);
        this.isSharedThemeEditor = !!options.isSharedThemeEditor;
        this.hideImportTheme = !!options.hideImportTheme;
        this.isStandaloneThemeEditor = !!options.isStandaloneThemeEditor;
        this.onClosed = options.onClosed;
        this.onSuccess = options.onSuccess;
        //this logic handles the case where user opens a team theme pane when viewing a presentation not utilizing a team theme.
        if (this.isSharedThemeEditor) {
            this.nonSharedTheme = app.currentTheme || null;
        }
        this.isTransitioning = false;
        this.theme = this.isSharedThemeEditor
            ? new SharedTheme(this.originalTheme.attributes, { disconnected: true, autoLoad: false })
            : new Theme(this.originalTheme.attributes, { disconnected: true, autoLoad: false });

        if (options.preimportTheme) {
            this.theme.set(options.preimportTheme.attributes);
        }

        this.loadThemePromise = this.theme.loadTheme()
            .then(() => {
                this.theme.styleSheet = deepClone(this.originalTheme.styleSheet);
                this.theme.styleSheet.palette = new Palette(this.theme);
                this.theme.decorationStyles = deepClone(this.originalTheme.decorationStyles);
                this.is_customized = false;
                app.currentTheme = this.theme;

                this._theme$ = new BehaviorSubject(this.theme);
                this.theme$ = this._theme$.asObservable();
                this.subs$ = new Subscription();
                this.subs$.add(this.theme$.subscribe(theme => this.addleButtons()));
            });
    },

    areAllFontsValid: function() {
        let valid = (
            !this.theme.get("originalFontHeaderFontId") &&
            !this.theme.get("originalFontTitleFontId") &&
            !this.theme.get("originalFontBodyFontId")
        );
        return valid;
    },

    addleButtons: function() {
        if (this.$save) {
            const currentJson = JSON.stringify(this.theme.attributes);
            let fontsValid = this.areAllFontsValid();
            let saveEnabled = (
                fontsValid &&
                (
                    this.isNewTheme ||
                    this.originalThemeJson !== currentJson
                )
            );
            if (saveEnabled) {
                this.$save.removeClass("disabled");
            } else {
                this.$save.addClass("disabled");
            }

            if (this.$saveToThemes) {
                if (fontsValid) {
                    this.$saveToThemes.removeClass("disabled");
                } else {
                    this.$saveToThemes.addClass("disabled");
                }
            }
        }
    },

    remove: function() {
        this.unmount && this.unmount();
        this.subs$.unsubscribe();
        Backbone.View.prototype.remove.apply(this, arguments);
    },

    updateTheme(deltaTheme) {
        this.theme.set(deltaTheme);
        this._theme$.next(this.theme);
    },

    posessive(name) {
        if (name.endsWith("'s")) {
            return name;
        } else if (name.endsWith("s")) {
            return name + "'";
        } else {
            return name + "'s";
        }
    },

    render: function() {
        this.$el.empty();

        this.$frame = this.$el.addEl($.div("theme-frame"));
        this.$editOptions = this.$frame.addEl($.div("editOptions"));

        this.loadThemePromise.then(() => {
            this.unmount = renderReactRoot(
                ThemeEditor,
                {
                    isSharedTheme: this.isSharedThemeEditor,
                    orgId: this.theme.get("orgId") || AppController.workspaceId,
                    theme$: this.theme$,
                    originalTheme: this.originalTheme.attributes,
                    hideImportTheme: this.hideImportTheme,
                    updateTheme: theme => this.updateTheme(theme),
                    shareFontsWithParent: fonts => {
                        this.fonts = fonts;
                        this.addleButtons();
                    },
                },
                this.$editOptions[0]
            );
        });

        this.$rightFrame = this.$frame.addEl($.div("right-frame"));

        this.$previewPane = this.$rightFrame.addEl($.div("preview_pane"));

        if (this.$tabBar) {
            this.$tabBar = null;
        }

        let $buttons = this.$rightFrame.addEl($.div("buttons"));

        if (!this.isSharedThemeEditor) {
            let className = "secondary";
            if (!this.areAllFontsValid()) {
                className += " disabled";
            }

            this.$saveToThemes = controls.createButton(this, {
                className,
                label: "Save to My Themes...",
                callback: () => this.saveThemeToLibrary()
            });
            $buttons.append(this.$saveToThemes);
        }

        $buttons.append($.div("spacer"));

        this.$cancel = controls.createButton(this, {
            label: this.isStandaloneThemeEditor ? "Close" : "Cancel",
            callback: () => this.cancel()
        });
        $buttons.append(this.$cancel);

        let cleanup = () => {
            this.close();
            this.onClosed && this.onClosed();
            this.onSuccess && this.onSuccess(this.theme);
        };

        let callback;
        if (this.isSharedThemeEditor) {
            callback = () => {
                const save = () => {
                    try {
                        this.saveSharedTeamTheme();
                        cleanup();
                    } catch (err) {
                        logger.error(err, "[ThemeEditor] saveSharedTeamTheme() failed");
                    }
                };

                if (this.isNewTheme) {
                    save();
                } else {
                    ShowConfirmationDialog({
                        title: "Are you sure you want to apply changes to this Team Theme?",
                        message: "These changes will be applied to all presentations that use this theme.",
                        acceptCallback: () => save(),
                    });
                }
            };
        } else {
            callback = () => {
                try {
                    this.applyThemeChanges(false);
                    cleanup();
                } catch (err) {
                    logger.error(err, "[ThemeEditor] applyThemeChanges() failed");
                }
            };
        }

        {
            let className = "accent primary";
            if (
                !this.isNewTheme ||
                !this.areAllFontsValid()
            ) {
                className += " disabled";
            }

            this.$save = controls.createButton(this, {
                label: this.isSharedThemeEditor ? "Share Theme" : "Done",
                callback,
                className,
            });
            $buttons.append(this.$save);
        }

        $(window).on("resize.editTheme", () => this.resize());
        $(window).on("keydown.editTheme", event => {
            if (event.which == 27) {
                this.cancel();
            }
        });

        return this;
    },

    switchTheme: function(title, onlyTeamThemes = false) {
        ShowDialog(ChooseThemeDialog, {
            title,
            hideUserThemes: onlyTeamThemes,
            hideBuiltInThemes: onlyTeamThemes,
            callback: theme => {
                ShowConfirmationDialog({
                    title: "Are you sure you want to replace the current theme?"
                }).then(isAccepted => {
                    if (isAccepted) {
                        const payload = {
                            ...theme.attributes,
                            ...(this.isSharedThemeEditor ? {
                                id: this.originalTheme.get("id"),
                                name: this.originalTheme.get("name"),
                                userId: null
                            } : {})
                        };

                        this.updateTheme(payload);
                        this.is_customized = false;

                        if (!this.isSharedThemeEditor && theme instanceof SharedTheme) {
                            this.theme = theme;
                            this.applyThemeChanges();
                        }
                    }
                });
            }
        });
    },

    saveThemeToLibrary: function() {
        let hasCustomFont = false;
        const styles = ["Body", "Title", "Header"];
        const workspaceId = UIController.getWorkspaceId();

        for (const style of styles) {
            const fontId = this.theme.get(`font${style}FontId`);
            if (fontId && !builtInFonts[fontId]) {
                hasCustomFont = true;
            } else {
                const obsoleteFontName = this.theme.get(`font${style}`);
                if (obsoleteFontName && !obsoleteBuiltInFontIds.includes(obsoleteFontName)) {
                    hasCustomFont = true;
                }
            }
        }
        if (hasCustomFont) {
            const customFontsEnabled = app.user.features.isFeatureEnabled(FeatureType.CUSTOM_FONTS, workspaceId);
            if (!customFontsEnabled) {
                ShowWarningDialog({
                    title: "Cannot Save Theme",
                    message: "This theme has custom fonts - in order to save this theme, you will need to upgrade your account.",
                });
                return;
            }
        }

        let isUserTheme = false;

        let themeName = "My Theme";

        const themeId = ds.selection.presentation
            ? ds.selection.presentation.get("themeId")
            : this.originalTheme.get("id");

        let sourceTheme = ds.userThemes.get(themeId);
        if (sourceTheme) {
            if (sourceTheme.get("isBuiltIn")) {
                themeName = "Copy of " + sourceTheme.get("name");
            } else {
                themeName = sourceTheme.get("name");
            }
        } else {
            sourceTheme = ds.builtInThemes.get(themeId);
            if (sourceTheme) {
                themeName = "Copy of " + sourceTheme.get("name");
            }
        }

        const orgId = workspaceId !== "personal" ? workspaceId : null;

        ShowDialog(InputConfirmationDialog, {
            title: "Save theme as...",
            okButtonLabel: "Save",
            input: {
                type: "text",
                placeholder: "Enter theme name...",
                value: this.isSharedThemeEditor ? `Copy of ${themeName}` : themeName
            },
            acceptCallback: async value => {
                let themeName = value;
                // reserved name for ui
                if (themeName === "Theme") {
                    ShowWarningDialog({
                        title: "Cannot Save Theme",
                        message: "You can't use \"Theme\" as your theme name.",
                    });
                    return;
                }
                //handles userThemes in org context OR userThemes in personal context
                let existingTheme = (
                    orgId
                        ? ds.userThemes.find({ name: themeName, orgId })
                        : ds.userThemes.find({ name: themeName })
                );
                if (existingTheme && !ds.userThemes.find({ name: themeName }).has("orgId")) {
                    ShowConfirmationDialog({
                        message: <Fragment>A theme named <strong>{themeName}</strong> already exists. Do you want to replace it?</Fragment>,
                        acceptCallback: async () => {
                            // Mitch - I'm commenting this canvas regeneration out because it doesnt work with refactor and i can't
                            // understand when this would be necessary upon overwriting an existing theme

                            // const progressDialog = ShowDialog(ProgressDialog, {
                            //     title: `Overwriting the ${themeName} theme`,
                            // });
                            const theme = ds.userThemes.saveUserTheme(this.theme, themeName, orgId);
                            // if (ds.selection.presentation) {
                            //     await app.themeManager.loadTheme(ds.selection.presentation);
                            //     for (let slide of app.mainView.editorView.slides) {
                            //         if (slide.canvas) {
                            //             slide.canvas.updateCanvasModel();
                            //         }
                            //     }
                            //     ds.selection.presentation.update({ themeId: theme.id });
                            // }
                            trackActivity("Theme", "OverwriteUserTheme", null, null, { theme_id: theme.id, theme_name: themeName }, { audit: false });
                            // progressDialog.props.closeDialog();
                        }
                    });
                } else {
                    const theme = ds.userThemes.saveUserTheme(this.theme, themeName, orgId);
                    if (ds.selection.presentation) {
                        ds.selection.presentation.update({ themeId: theme.id });
                    }
                    trackActivity("Theme", "NewUserTheme", null, null, { theme_id: theme.id, theme_name: themeName }, { audit: false });
                }
            }
        });
    },

    applyThemeChanges: function(close = true) {
        const old_theme_name = this.originalTheme.get("name");
        const old_theme_type = this.originalTheme.get("isBuiltIn") ? "system" : (this.originalTheme.get("orgId") ? "team" : "user");
        const new_theme_type = this.theme.get("isBuiltIn") ? "system" : (this.theme.get("orgId") ? "team" : "user");
        const new_theme_name = this.theme.get("name");

        const props = {
            old_theme_name,
            old_theme_type,
            new_theme_name,
            new_theme_type,
            is_customized: this.is_customized
        };
        trackActivity("Presentation", "ThemeSelected", null, null, props);
        if (close) {
            this.close();
        }
    },

    saveSharedTeamTheme: function() {
        const themeName = this.theme.get("name");
        const progressDialog = ShowDialog(ProgressDialog, {
            title: `Overwriting the ${themeName} theme`,
        });
        ds.sharedThemes.saveSharedTheme(this.theme, themeName, this.theme.get("id"));
        if (ds.selection &&
            ds.selection.presentation &&
            ds.selection.presentation.has("sharedThemeId") &&
            ds.selection.presentation.get("orgId") === this.theme.get("orgId")) {
            this.applyThemeChanges();
            ds.selection.presentation.trigger("sharedThemeUpdated", ds.selection.presentation);
        }
        progressDialog.props.closeDialog();
    },

    renameTheme: function() {
        const theme = this.theme;
        ShowDialog(InputConfirmationDialog, {
            title: "Rename Theme",
            message: null,
            input: {
                value: theme.get("name")
            },
            acceptCallback: value => {
                try {
                    theme.update({ name: value.trim() });
                    this.$themeName.text(value.trim());
                } catch (err) {
                    ShowErrorDialog({
                        title: "Unable to rename theme",
                        message: err.message
                    });
                }
            },
        });
    },

    saveCopyOfTeamTheme: function() {
        const orgId = AppController.orgId;
        const theme = this.theme;

        const workspaceId = UIController.getWorkspaceId();
        const team = ds.teams.defaultTeamForOrg(workspaceId);

        ShowDialog(InputConfirmationDialog, {
            title: "Save copy",
            message: null,
            input: {
                value: "Copy of " + theme.get("name"),
            },
            acceptCallback: value => {
                try {
                    const name = value;
                    app.themeManager.createTeamTheme(theme.attributes, orgId, team, name);
                } catch (err) {
                    ShowErrorDialog({
                        title: "Unable to save theme",
                        message: err.message
                    });
                }
            },
        });
    },

    cancel: async function() {
        let theme = this.isSharedThemeEditor ? this.nonSharedTheme : this.originalTheme;
        if (theme) {
            await theme.loadTheme();
            app.currentTheme = theme;
        }
        this.close();
        this.onClosed && this.onClosed();
    },

    show: function() {
        $("body").append(this.render().$el);
        app.dialogManager.registerDialog(this);
        this.renderPreviewPane()
            // Delay the resize a frame so the render can finish mounting.
            .then(() => setTimeout(() => this.resize(), 0));
    },

    close: async function() {
        $(window).off(".editTheme");
        this.stopListening(this.theme, "all");

        // Ensure we don't close until the canvases
        //   are done loading to avoid errors
        await this.canvasBundleLoadPromise;
        await this.previewsLoadPromise;

        this.$el.remove();

        app.dialogManager.unregisterDialog(this);
    },

    resize: function() {
        this.layoutPreviewPane();
    },

    renderPreviewPane: async function() {
        this.slideCanvases = [];

        const { SlideCanvas } = await this.canvasBundleLoadPromise;
        this.pendingRenders = [];

        let $mainPreview = this.renderPreview(PreviewSlide1, SlideCanvas);
        $mainPreview.addClass("preview main_preview");
        this.$previewPane.append($mainPreview);

        let $thumbnail = this.renderPreview(PreviewSlide2, SlideCanvas);
        $thumbnail.addClass("preview alt_preview");
        this.$previewPane.append($thumbnail);

        $thumbnail = this.renderPreview(PreviewSlide3, SlideCanvas);
        $thumbnail.addClass("preview alt_preview");
        this.$previewPane.append($thumbnail);

        $thumbnail = this.renderPreview(PreviewSlide4, SlideCanvas);
        $thumbnail.addClass("preview alt_preview");
        this.$previewPane.append($thumbnail);

        $thumbnail = this.renderPreview(PreviewSlide5, SlideCanvas);
        $thumbnail.addClass("preview alt_preview");
        this.$previewPane.append($thumbnail);

        $thumbnail = this.renderPreview(PreviewSlide6, SlideCanvas);
        $thumbnail.addClass("preview alt_preview");
        this.$previewPane.append($thumbnail);

        this.previewsLoadPromise = Promise.allSettled(this.pendingRenders);

        this.listenTo(this.theme, "all", () => {
            const refreshCanvas = canvas =>
                canvas.loadStyles(true)
                    .then(() => canvas.layouter.canvasElement.markStylesAsDirty())
                    .then(() => canvas.refreshCanvasAutoRevert())
                    .catch(err => logger.error(err, "[EditThemePanel] canvas.loadStyles() failed"));

            this.is_customized = true;
            this.theme.loadTheme().then(() => {
                let canvas = this.$el.find(".main_preview").data("canvas");
                if (canvas) {
                    refreshCanvas(canvas)
                        .then(() => {
                            this.$el.find(".alt_preview").each((index, preview) => {
                                const canvas = $(preview).data("canvas");
                                if (canvas) {
                                    refreshCanvas(canvas);
                                }
                            });
                        });
                }
            });
        });
    },

    layoutPreviewPane: function() {
        let width = this.$previewPane.width();
        let height = this.$previewPane.height();

        this.previewScale = geom.fitImageToRect(new geom.Size(1280, 720 + 220), new geom.Size(width, height));
        this.previewScale = Math.min(1, this.previewScale);

        let largePreviewWidth = 1280 * this.previewScale;
        let largePreviewHeight = 720 * this.previewScale;

        let smallPreviewGap = 20;
        let smallPreviewWidth = (largePreviewWidth - (smallPreviewGap * 4)) / 5;
        let smallPreviewScale = smallPreviewWidth / 1280;
        let smallPreviewHeight = 720 * smallPreviewScale;

        this.previewOffsetX = width / 2 - largePreviewWidth / 2;

        this.$previewPane.find(".main_preview").velocity({
            translateX: this.previewOffsetX,
            translateY: 0,
            scale: this.previewScale
        }, {
            duration: 0
        });

        for (let i = 0; i < 5; i++) {
            $(this.$previewPane.find(".alt_preview")[i]).velocity({
                translateX: this.previewOffsetX + i * (smallPreviewWidth + 20),
                translateY: largePreviewHeight + 20,
                scale: smallPreviewScale
            }, {
                duration: 0
            });
        }
    },

    renderPreview: function(slideModel, SlideCanvas) {
        let model = new Slide(slideModel, { disconnected: true });

        let thumbnailCanvas = new SlideCanvas({
            dataModel: model,
            canvasWidth: this.CANVAS_WIDTH,
            canvasHeight: this.CANVAS_HEIGHT,
            editable: false,
        });

        this.slideCanvases.push(thumbnailCanvas);
        thumbnailCanvas.render();
        this.pendingRenders.push(thumbnailCanvas.renderSlide());

        thumbnailCanvas.$el.data("canvas", thumbnailCanvas);
        thumbnailCanvas.$el.on("click", event => this.selectPreview(event));

        return thumbnailCanvas.$el;
    },

    selectPreview: function(event) {
        if (!$(event.currentTarget).hasClass("main_preview") && !this.isTransitioning) {
            let transform = $(".main_preview").transform();

            let $preview = $(event.currentTarget);
            $(".main_preview").removeClass("main_preview").addClass("alt_preview").velocity({
                translateX: $preview.transform().x,
                translateY: $preview.transform().y,
                scale: $preview.transform().scale
            });
            $preview.removeClass("alt_preview").addClass("main_preview").left(0);

            $preview.velocity({
                translateX: transform.x,
                translateY: transform.y,
                scale: transform.scale
            }, {
                begin: () => {
                    this.isTransitioning = true;
                },
                complete: () => {
                    this.isTransitioning = false;
                }
            });
        }
    }
});
